import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, 
  Instagram, 
  Linkedin, 
  Twitter, 
  Facebook,
  AtSign,
  Plus, 
  Trash2, 
  ExternalLink,
  Upload,
  Image as ImageIcon,
  Camera,
  Loader2,
  X,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { db, auth, storage } from '../firebase';
import { collection, onSnapshot, query, orderBy, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { enhanceShowcaseDescription } from '../lib/gemini';

import { handleFirestoreError, OperationType } from '../lib/firestore-errors';

interface ShowcaseItem {
  id?: string;
  platform: 'ig' | 'li' | 'x' | 'fb' | 'tr';
  imageUrl: string;
  imageUrls?: string[];
  title: string;
  brandName: string;
  description: string;
  createdAt: any;
}

import { useTranslation } from '../hooks/useTranslation';

export function Showcase() {
  const { t, isBrazil } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'ig' | 'li' | 'x' | 'fb' | 'tr'>('all');
  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newItem, setNewItem] = useState<Partial<ShowcaseItem>>({
    platform: 'ig',
    imageUrl: '',
    imageUrls: [],
    title: '',
    brandName: '',
    description: ''
  });

  const isAdmin = auth.currentUser?.email === "dewaldjordaan5@gmail.com";

  const handleAIEnhance = async () => {
    if (!newItem.description && !newItem.title) return;
    setIsEnhancing(true);
    
    try {
      const newDescription = await enhanceShowcaseDescription(newItem.brandName || '', newItem.title || '', newItem.description || '');
      if (newDescription) {
        setNewItem(prev => ({ ...prev, description: newDescription }));
      }
    } catch (err) {
      console.error("AI Enhance failed:", err);
    } finally {
      setIsEnhancing(false);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'showcase_items'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShowcaseItem));
      setItems(data);
    }, (err) => {
      handleFirestoreError(err, OperationType.GET, 'showcase_items');
    });
    return () => unsubscribe();
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !isAdmin) return;

    setIsUploading(true);
    try {
      const urls = await Promise.all(
        Array.from(files).map(async (file) => {
          const storageRef = ref(storage, `showcase/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        })
      );
      setNewItem(prev => ({ 
        ...prev, 
        imageUrl: urls[0],
        imageUrls: [...(prev.imageUrls || []), ...urls]
      }));
    } catch (err) {
      console.error("Error uploading image:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      await addDoc(collection(db, 'showcase_items'), {
        ...newItem,
        createdAt: new Date()
      });
      setShowAddForm(false);
      setNewItem({ platform: 'ig', imageUrl: '', imageUrls: [], title: '', brandName: '', description: '' });
    } catch (err) {
      console.error("Error adding showcase item:", err);
    }
  };

  const handleDeleteItem = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin || !window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await deleteDoc(doc(db, 'showcase_items', id));
    } catch (err) {
      console.error("Error deleting showcase item:", err);
    }
  };

  const filteredItems = filter === 'all' ? items : items.filter(item => item.platform === filter);

  return (
    <div className="min-h-screen bg-[#030303] pt-24">
      <Helmet>
        <title>{isBrazil ? "Cases de Sucesso" : "Showcase"} | PlataPost AI</title>
        <meta name="description" content={isBrazil ? "Veja exemplos de conteúdo de alta qualidade gerados pela PlataPost AI." : "See samples of high-end social media content generated by PlataPost AI for various brands."} />
      </Helmet>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <header className="text-center mb-16 space-y-4">
          <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
            Showcase
          </p>
          <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">
            {isBrazil ? "Excelência Gerada por" : "AI-Generated"} <span className="text-indigo-500">{isBrazil ? "IA" : "Excellence."}</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-medium">
            {isBrazil ? "Explore amostras de conteúdo otimizado para plataformas gerado para nossas marcas parceiras usando a tecnologia Brand Brain da PlataPost." : "Explore samples of platform-optimized content generated for our partner brands using PlataPost's Brand Brain technology."}
          </p>
          {isAdmin && (
            <button 
              onClick={() => setShowAddForm(true)}
              className="mt-8 px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-white font-black flex items-center gap-2 mx-auto hover:bg-white/10 transition-all text-sm uppercase tracking-widest"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
          )}
        </header>

        <div className="flex flex-wrap justify-center gap-3 mb-16">
          <button 
            onClick={() => setFilter('all')}
            className={`px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${filter === 'all' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
          >
            {isBrazil ? "Todos" : "All"}
          </button>
          <button 
            onClick={() => setFilter('ig')}
            className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${filter === 'ig' ? 'bg-pink-600 text-white shadow-lg shadow-pink-600/20' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
          >
            <Instagram className="w-4 h-4" /> Instagram
          </button>
          <button 
            onClick={() => setFilter('li')}
            className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${filter === 'li' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
          >
            <Linkedin className="w-4 h-4" /> LinkedIn
          </button>
          <button 
            onClick={() => setFilter('x')}
            className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${filter === 'x' ? 'bg-white text-black shadow-lg shadow-white/20' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
          >
            <Twitter className="w-4 h-4" /> X
          </button>
          <button 
            onClick={() => setFilter('fb')}
            className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${filter === 'fb' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
          >
            <Facebook className="w-4 h-4" /> Facebook
          </button>
          <button 
            onClick={() => setFilter('tr')}
            className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${filter === 'tr' ? 'bg-black text-white shadow-lg shadow-white/20 border-white' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
          >
            <AtSign className="w-4 h-4" /> Threads
          </button>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-32 glass-card rounded-[3rem] border border-white/5">
            <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-white/10">
               <ImageIcon className="w-10 h-10 text-gray-600" />
            </div>
            <p className="text-gray-500 font-bold tracking-tight">No samples curated yet.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
            {filteredItems.map(item => (
              <motion.div 
                key={item.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="group border border-white/5 rounded-[2rem] hover:bg-white/5 transition-all p-4"
              >
                <Link to={`/showcase/${item.id}`} className="block">
                  <div className="relative aspect-square overflow-hidden rounded-[1.5rem] border border-white/5 mb-6">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                      referrerPolicy="no-referrer" 
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white shadow-2xl backdrop-blur-md ${
                        item.platform === 'ig' ? 'bg-gradient-to-tr from-yellow-400/80 via-red-500/80 to-purple-600/80 border border-white/20' :
                        item.platform === 'li' ? 'bg-blue-600/80 border border-white/20' :
                        item.platform === 'fb' ? 'bg-[#1877F2]/80 border border-white/20' :
                        item.platform === 'tr' ? 'bg-black/80 border border-white/40' :
                        'bg-black/80 border border-white/20'
                      }`}>
                        {item.platform === 'ig' ? 'Instagram' : item.platform === 'li' ? 'LinkedIn' : item.platform === 'fb' ? 'Facebook' : item.platform === 'tr' ? 'Threads' : 'X'}
                      </span>
                    </div>
                    {item.imageUrls && item.imageUrls.length > 1 && (
                      <div className="absolute bottom-4 left-4 bg-black/80 px-3 py-1 rounded-full text-xs font-bold text-white border border-white/20">
                        +{item.imageUrls.length - 1} images
                      </div>
                    )}
                    {isAdmin && (
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeleteItem(item.id!, e);
                        }}
                        className="absolute top-4 right-4 p-3 bg-red-600 text-white rounded-2xl opacity-0 group-hover:opacity-100 transition-all hover:bg-red-700 shadow-xl"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-4 px-2 pb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-3 py-1 rounded-lg border border-indigo-500/10">{item.brandName}</span>
                    </div>
                    <h3 className="text-xl font-black text-white leading-tight tracking-tight">{item.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-3 font-medium leading-relaxed">{item.description}</p>
                    <span className="text-indigo-400 text-xs font-bold mt-2 inline-block">Read study &rarr;</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Add Form Modal */}
      <AnimatePresence>
        {showAddForm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[100] flex items-center justify-center p-6 overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-card rounded-[3rem] shadow-2xl w-full max-w-lg my-auto overflow-hidden border border-white/10"
            >
              <div className="p-8 border-b border-white/5 flex items-center justify-between bg-white/5">
                <h2 className="text-xl font-black text-white lowercase tracking-tight">New showcase item</h2>
                <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-all">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleAddItem} className="p-10 space-y-8">
                {/* Image Upload Area */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Post Preview</label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative w-full aspect-square rounded-[2.5rem] border-2 border-dashed transition-all flex flex-col items-center justify-center gap-4 cursor-pointer overflow-hidden ${
                      newItem.imageUrl ? 'border-indigo-600 bg-indigo-600/5' : 'border-white/10 hover:border-indigo-500/50 hover:bg-white/5'
                    }`}
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                        <span className="text-sm font-black text-gray-500 italic uppercase tracking-widest">Uploading excellence...</span>
                      </div>
                    ) : newItem.imageUrl ? (
                      <>
                        <img src={newItem.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-indigo-600/60 flex items-center justify-center opacity-0 hover:opacity-100 transition-all backdrop-blur-sm">
                          <Camera className="w-10 h-10 text-white" />
                        </div>
                        {newItem.imageUrls && newItem.imageUrls.length > 1 && (
                          <div className="absolute bottom-4 right-4 bg-black/80 px-3 py-1 rounded-full text-xs font-bold text-white border border-white/20">
                            +{newItem.imageUrls.length - 1} images
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center border border-white/10 group-hover:scale-110 transition-transform">
                          <ImageIcon className="w-10 h-10 text-gray-600" />
                        </div>
                        <div className="text-center px-6">
                          <p className="text-sm font-black text-white uppercase tracking-widest">Upload post visual</p>
                          <p className="text-xs text-gray-500 mt-2 font-medium">PNG, JPG or WebP (Max 5MB)</p>
                        </div>
                      </>
                    )}
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleImageUpload} 
                      className="hidden" 
                      accept="image/*" 
                      multiple
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Platform</label>
                    <select 
                      value={newItem.platform}
                      onChange={e => setNewItem({...newItem, platform: e.target.value as any})}
                      className="w-full px-6 py-4 rounded-[1.5rem] bg-white/5 border border-white/10 text-white outline-none focus:ring-2 focus:ring-indigo-600 transition-all font-black uppercase text-xs tracking-widest appearance-none"
                    >
                      <option value="ig">Instagram</option>
                      <option value="li">LinkedIn</option>
                      <option value="x">X (Twitter)</option>
                      <option value="fb">Facebook</option>
                      <option value="tr">Threads</option>
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Brand</label>
                    <input 
                      type="text"
                      value={newItem.brandName}
                      onChange={e => setNewItem({...newItem, brandName: e.target.value})}
                      placeholder="Brand Name"
                      className="w-full px-6 py-4 rounded-[1.5rem] bg-white/5 border border-white/10 text-white outline-none focus:ring-2 focus:ring-indigo-600 transition-all font-bold placeholder:text-gray-700"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Headline</label>
                  <input 
                    type="text"
                    value={newItem.title}
                    onChange={e => setNewItem({...newItem, title: e.target.value})}
                    placeholder="Short title for this sample"
                    className="w-full px-6 py-4 rounded-[1.5rem] bg-white/5 border border-white/10 text-white outline-none focus:ring-2 focus:ring-indigo-600 transition-all font-black tracking-tight placeholder:text-gray-700"
                    required
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Strategy description (Markdown)</label>
                    <button
                      type="button"
                      onClick={handleAIEnhance}
                      disabled={isEnhancing || (!newItem.description && !newItem.title)}
                      className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50"
                    >
                      {isEnhancing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      AI Enhance for SEO
                    </button>
                  </div>
                  <textarea 
                    value={newItem.description}
                    onChange={e => setNewItem({...newItem, description: e.target.value})}
                    placeholder="Write your case study using Markdown..."
                    className="w-full px-6 py-4 rounded-[1.5rem] bg-white/5 border border-white/10 text-white outline-none focus:ring-2 focus:ring-indigo-600 transition-all min-h-[250px] font-medium leading-relaxed placeholder:text-gray-700"
                    required
                  />
                </div>

                <div className="pt-6 flex gap-6">
                  <button 
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 px-8 py-5 rounded-[1.5rem] font-black uppercase text-xs tracking-widest bg-white/5 text-gray-400 hover:text-white transition-all shadow-sm"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={isUploading || !newItem.imageUrl}
                    className="flex-[2] bg-indigo-600 text-white py-5 rounded-[1.5rem] font-black uppercase text-xs tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                  >
                    {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    Add Item
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
